import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import {
  colors2,
  UI_BODY_SM_DF,
  UI_CAPTION,
} from "oolib";
import { ButtonPrimary } from "oolib/dist/v2/components/Buttons";
import axios from "axios";
import { IntegrationDisconnectConfirmModal } from "@/_self_managed/SM_Integrations/IntegrationDisconnectConfirmModal";

// ============================================
// STYLED COMPONENTS
// ============================================

const OAuthStatusBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 6px;
  background: ${(props) => (props.$connected ? colors2.green5 : colors2.grey5)};
  border: 1px solid ${(props) => (props.$connected ? colors2.green20 : colors2.grey10)};
  margin-bottom: 16px;
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(props) => (props.$connected ? colors2.green : colors2.grey40)};
`;

const StatusText = styled(UI_BODY_SM_DF)`
  color: ${(props) => (props.$connected ? colors2.green60 : colors2.grey60)};
`;

const ErrorText = styled(UI_CAPTION)`
  color: ${colors2.red};
  margin-top: 8px;
`;

// ============================================
// BASE OAUTH INTERFACE
// ============================================

/**
 * BaseOAuthInterface
 *
 * Reusable component for OAuth-based integrations (Zoom, Reddit).
 * Handles the OAuth popup flow and provides the access token to children.
 *
 * Props:
 * - providerId: string - OAuth provider ID (e.g., "zoom", "reddit")
 * - providerName: string - Display name (e.g., "Zoom")
 * - children: Function(accessToken) - Render prop called with token once connected
 * - onDisconnect: Function - Optional callback when user disconnects
 */
export const BaseOAuthInterface = ({
  providerId,
  providerName,
  children,
}) => {
  const [accessToken, setAccessToken] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  // Listen for postMessage from OAuth popup
  const handleMessage = useCallback(
    (event) => {
      if (event.data?.type === "OAUTH_CALLBACK") {
        if (event.data.success && event.data.accessToken) {
          setAccessToken(event.data.accessToken);
          setError(null);
        } else {
          setError(event.data.error || "Authorization failed");
        }
        setIsConnecting(false);
      }
    },
    []
  );

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [handleMessage]);

  // Also check URL params for redirect-based flow
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("oauthToken");
    const provider = urlParams.get("provider");
    if (token && provider === providerId) {
      setAccessToken(token);
      // Clean URL params
      const url = new URL(window.location);
      url.searchParams.delete("oauthToken");
      url.searchParams.delete("provider");
      window.history.replaceState({}, "", url);
    }
  }, [providerId]);

  const handleConnect = async () => {
    setIsConnecting(true);
    setError(null);

    try {
      const response = await axios.post("/api/integrationOAuth/initiate", {
        provider: providerId,
        frontendCallbackUrl: window.location.href,
      });

      if (response.data.success && response.data.authUrl) {
        // Open OAuth URL in popup
        const width = 600;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;

        window.open(
          response.data.authUrl,
          `${providerId}_oauth`,
          `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
        );
      } else {
        setError("Failed to initiate OAuth flow");
        setIsConnecting(false);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Failed to connect");
      setIsConnecting(false);
    }
  };

  const handleDisconnect = () => {
    setAccessToken(null);
    setError(null);
  };

  // Not connected yet - show connect button
  if (!accessToken) {
    return (
      <>
        <OAuthStatusBar $connected={false}>
          <StatusDot $connected={false} />
          <StatusText $connected={false}>Not connected</StatusText>
        </OAuthStatusBar>

        <ButtonPrimary
          onClick={handleConnect}
          disabled={isConnecting}
          isLoading={isConnecting}
        >
          Connect with {providerName}
        </ButtonPrimary>

        {error && <ErrorText>{error}</ErrorText>}
      </>
    );
  }

  // Connected - show status + render children with token
  return (
    <>
      <OAuthStatusBar $connected={true}>
        <StatusDot $connected={true} />
        <StatusText $connected={true}>
          Connected to {providerName}
        </StatusText>
        <span
          onClick={() => setShowDisconnectConfirm(true)}
          style={{
            marginLeft: "auto",
            color: colors2.grey40,
            cursor: "pointer",
            fontSize: "12px",
            textDecoration: "underline",
          }}
        >
          Disconnect
        </span>
      </OAuthStatusBar>

      {showDisconnectConfirm && (
        <IntegrationDisconnectConfirmModal
          name={providerName}
          onConfirm={() => {
            handleDisconnect();
            setShowDisconnectConfirm(false);
          }}
          onClose={() => setShowDisconnectConfirm(false)}
        />
      )}

      {children(accessToken)}
    </>
  );
};

export default BaseOAuthInterface;
